/**
 * The firm dashboard (Phase 4B): one page for partners, executives and IT leads. Baseline KPIs as rings
 * against the network median, the areas that need attention first (ranked by risk), action plans with a
 * roadmap the IT lead can present to leadership, and the risks the assessment found. Plain language; every
 * number says what it measures; terms are explained in the side panel instead of on other pages.
 * National IT and the Board open the same page read-only.
 */
import { RISK_LABEL, RISK_SEVERITY } from '@grc/shared/vocabulary';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useApi, type Loadable } from '../api';
import { Bullet, ChartTable, fullyText, InPlaceKey, InPlaceSplit, inPlaceSummary, partlyText, Ring } from '../charts';
import { Card, formatDate, Load, PageBack, PageHeader } from '../components';
import { ExplainedPanel, WithPanel } from '../Explained';
import { useBackTo } from '../origin';
import { useViewParam, ViewTabs } from '../tabs';
import type { AreaRow, BaselineScore, FirmOverview as Overview, Me } from '../types';
import { ActionPlanList, PlanRoadmap } from './ActionPlans';
import { FirmGapsNationalCanHelp } from './NationalCoverage';
import { FirmProgramCard } from './Program';

export const RESPONSE_STATE: Record<string, string> = {
  not_started: 'No response yet',
  mitigating: 'Being addressed',
  mitigated: 'Addressed',
  accepted: 'Accepted',
};

const pct = (v: number | null) => (v === null ? 'n/a' : `${Math.round(v)}%`);

export function SeverityChip({ severity }: { severity: string }) {
  return <span className={`sev sev-${severity}`}>{RISK_SEVERITY[severity]?.label ?? severity}</span>;
}

export function NetworkBanner({ viewer, firmName }: { viewer: 'firm' | 'network'; firmName: string }) {
  const me = useOutletContext<Loadable<Me> | undefined>();
  const { firmCode } = useParams();
  // Back where the firm was opened from (the National view, or Leadership with its tab and firm); else by tier:
  // National IT to the National view, the Board to Leadership's network tab (DEC-22, DEC-33). No link until known (CP-04).
  const back = useBackTo(firmCode ?? firmName, me?.data ? { national: me.data.tiers.national } : null);
  if (viewer !== 'network') return null;
  return (
    <p className="notice small" role="note">
      You are viewing {firmName} as National IT or the Board: read-only. The firm owns these results and decisions.{' '}
      {back && <Link to={back.to}>{back.label}</Link>}
    </p>
  );
}

/** Unsent drafts (DEC-31), counted by the API with the worklist's own rule (shared workState). */
export const drafts = (o: Overview) => o.selfAssessment.states.draft;

/** The firm dashboard in four tabs. Existing content moves under them; nothing new is added. */
const FIRM_VIEWS = [
  { id: 'summary', label: 'Summary' },
  { id: 'self', label: 'Self-assessment', short: 'Self' },
  { id: 'plans', label: 'Action plan and roadmap', short: 'Plans' },
  { id: 'risks', label: 'Risks identified', short: 'Risks' },
] as const;

/** A count that opens its list, or plain text when there is nothing to open. */
function CountLink({ n, to, one, many }: { n: number; to: string; one: string; many: string }) {
  const text = `${n} ${n === 1 ? one : many}`;
  return n > 0 ? <Link to={to}>{text}</Link> : <span className="muted">{text}</span>;
}

export function FirmOverview() {
  const { firmCode } = useParams();
  const state = useApi<Overview>(`/firms/${firmCode}/overview`);
  const { view, setView, liveView } = useViewParam(FIRM_VIEWS, 'summary');
  return (
    <Load state={state} what="the dashboard">
      {(o) => {
        const self = o.source === 'self';
        const b = o.baselines;
        const survey = b?.source === 'survey2025';
        return (
          <>
            <NetworkBanner viewer={o.viewer} firmName={o.firmName} />
            {/* A network reader already has a way back that waits until their tier is known (CP-04). */}
            {o.viewer !== 'network' && <PageBack to="/">Back to Home</PageBack>}
            <PageHeader eyebrow={o.firmName} title="Cyber resilience dashboard" lede="Where the firm stands, what needs attention first, and the plan to get there." />
            <ViewTabs views={FIRM_VIEWS} view={view} onSelect={setView} liveView={liveView} label="Firm dashboard" idPrefix="firm" panelId={(t) => `firm-panel-${t}`} />
            <WithPanel panel={<ExplainedPanel />}>
              <div id="firm-panel-summary" role="tabpanel" aria-labelledby="firm-tab-summary" tabIndex={0} hidden={view !== 'summary'}>
              {self && (
                <p className="notice small">
                  These figures come from the firm's own 2026 self-assessment ({o.selfAssessment.rated} of {o.selfAssessment.total} controls rated,{' '}
                  {o.selfAssessment.states.reviewed} reviewed by National IT). The independent assessment will replace them.
                </p>
              )}
              {survey && (
                <p className="notice small">
                  No 2026 results yet. The baselines below are as of March 2025, worked out from the firm's own survey answers: a starting point, not an
                  independent result. The firm's 2026 self-assessment is on the Self-assessment tab.
                </p>
              )}
              <div className="cards kpis">
                {b && survey ? (
                  <>
                    <SurveyKpiCard score={b.btc} measure={b.measure} />
                    <SurveyKpiCard score={b.bti} measure={b.measure} />
                  </>
                ) : b ? (
                  <>
                    <KpiCard score={b.btc} />
                    <KpiCard score={b.bti} />
                  </>
                ) : (
                  <Card title="Baselines">
                    <p>No results yet. Start the self-assessment to see where the firm stands.</p>
                  </Card>
                )}
                <section className="card" aria-labelledby="kpi-maturity">
                  <h2 id="kpi-maturity">Maturity (0 to 5)</h2>
                  <figure className="measure">
                    <div className="measure-value hero">{o.maturity.value === null ? 'Not rated' : o.maturity.value.toFixed(1)}</div>
                    <Bullet
                      pct={o.maturity.value === null ? null : (o.maturity.value / 5) * 100}
                      median={o.maturity.networkMedian === null ? null : (o.maturity.networkMedian / 5) * 100}
                      label={`Maturity ${o.maturity.value?.toFixed(1) ?? 'not rated'} of 5${o.maturity.networkMedian !== null ? `, network median ${o.maturity.networkMedian.toFixed(1)}` : ''}`}
                    />
                    <figcaption>
                      How established the practices are, averaged across areas. The baselines count a control as in place when it is fully or partly in place; maturity shows how much further each has to go.
                      {o.maturity.networkMedian !== null && ` Network median ${o.maturity.networkMedian.toFixed(1)}.`}
                      {survey && ' The March 2025 survey did not ask about maturity.'}
                    </figcaption>
                  </figure>
                </section>
              </div>

              {o.program && <FirmProgramCard program={o.program} />}

              <Card title="Areas, highest priority first">
                <p className="muted small">{o.areas.measure}</p>
                <AreasTable rows={o.areas.rows} firmCode={o.firmCode} />
              </Card>
              {/* Last on the summary (Andres, 2026-09-24): the firm's own open gaps and the National services that help. */}
              <FirmGapsNationalCanHelp firm={{ firmCode: o.firmCode, displayName: o.firmName, canWrite: o.canUpdate }} />
              </div>

              <div id="firm-panel-self" role="tabpanel" aria-labelledby="firm-tab-self" tabIndex={0} hidden={view !== 'self'}>
                <section className="card self-box" aria-labelledby="kpi-self">
                  <h2 id="kpi-self">Self-assessment</h2>
                  <figure className="measure">
                    <div className="measure-value hero">
                      {o.selfAssessment.rated} <span className="muted small">of {o.selfAssessment.total} rated by the firm</span>
                    </div>
                    <Bullet pct={(o.selfAssessment.rated / o.selfAssessment.total) * 100} label={`${o.selfAssessment.rated} of ${o.selfAssessment.total} controls rated by the firm`} />
                    <figcaption>
                      {/* Each count opens its filter on the worklist. */}
                      <ul className="plain sa-counts small">
                        <li>
                          <CountLink n={o.selfAssessment.states.returned} to={`/firm/${o.firmCode}/controls?view=worklist&state=returned`} one="returned with questions" many="returned with questions" />
                        </li>
                        <li>
                          <CountLink n={drafts(o)} to={`/firm/${o.firmCode}/controls?view=worklist&state=draft`} one="rating not sent to National IT" many="ratings not sent to National IT" />
                        </li>
                        <li>
                          <CountLink n={o.selfAssessment.states.awaiting} to={`/firm/${o.firmCode}/controls?view=worklist&state=awaiting`} one="awaiting review" many="awaiting review" />
                        </li>
                        <li>{o.selfAssessment.states.reviewed} reviewed by National IT</li>
                      </ul>
                    </figcaption>
                  </figure>
                  {o.canUpdate && (
                    <ul className="plain sa-links small">
                      <li>
                        {/* Opens where the work is: returned questions first, then controls still to rate. */}
                        <Link
                          data-tour="self-start"
                          to={`/firm/${o.firmCode}/controls?view=worklist${o.selfAssessment.states.returned ? '&state=returned' : drafts(o) ? '&state=draft' : o.selfAssessment.states.not_rated ? '&state=not_rated' : ''}`}
                        >
                          {o.selfAssessment.rated ? 'Continue the self-assessment' : 'Start the self-assessment'}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/firm/${o.firmCode}/controls?view=worklist&wave=2`}>Wave 2 controls</Link>
                      </li>
                      <li>
                        <Link to={`/firm/${o.firmCode}/policies`}>Policies</Link>
                      </li>
                      <li>
                        <Link to={`/guidance/resources?firm=${o.firmCode}#ask`}>Ask National</Link>
                      </li>
                    </ul>
                  )}
                </section>
              </div>

              <div id="firm-panel-plans" role="tabpanel" aria-labelledby="firm-tab-plans" tabIndex={0} hidden={view !== 'plans'}>
              <Card title="Action plans and roadmap">
                <p className="muted small">{o.actionPlans.measure}. Dates and progress are kept current by the firm's IT lead.</p>
                <PlanRoadmap plans={o.actionPlans.plans} />
                <ActionPlanList firmCode={o.firmCode} plans={o.actionPlans.plans} canUpdate={o.canUpdate} risks={o.risks.list} onChanged={state.reload} />
              </Card>
              </div>

              <div id="firm-panel-risks" role="tabpanel" aria-labelledby="firm-tab-risks" tabIndex={0} hidden={view !== 'risks'}>
              <Card title="Risks identified">
                <p className="muted small">
                  {o.risks.measure}
                  {o.risks.identifiedOn && `, identified ${formatDate(o.risks.identifiedOn)}`}.
                </p>
                {o.risks.list.length ? (
                  <ul className="plain risk-list">
                    {o.risks.list.map((r) => (
                      <li key={r.id}>
                        <SeverityChip severity={r.severity} />
                        <span className="risk-title">
                          <span className="cid">{r.code}</span> {r.title}
                        </span>
                        <span className="muted small">
                          {r.accepted ? 'Accepted' : RESPONSE_STATE[r.responseState ?? 'not_started']}
                          {r.plans > 0 && `, with ${r.plans} action plan${r.plans > 1 ? 's' : ''}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No risk assessment report in the portal yet.</p>
                )}
                <p>
                  <Link className="pill" to={`/firm/${o.firmCode}/risks`}>
                    Open the risk register
                  </Link>{' '}
                  <Link className="pill secondary-dark" to={`/firm/${o.firmCode}/controls`}>
                    See each control
                  </Link>
                </p>
              </Card>
              </div>
            </WithPanel>
          </>
        );
      }}
    </Load>
  );
}

/**
 * A baseline from current results: "In place: 34 of 56 (61%)" counting fully and partly in place, then the split
 * directly under it so the share only partly done stays visible. Maturity is what shows how established it is.
 */
function KpiCard({ score }: { score: BaselineScore }) {
  const { name: title, pct: p, inPlace, assessed: total, networkMedian: median, what: note } = score;
  const partly = score.partly ?? 0;
  const fully = score.fully ?? inPlace - partly;
  const notYet = score.notYet ?? total - inPlace;
  // Points of the in-place share only partly done: the ring draws them in their own colour, and its centre
  // shows the fully figure under the headline, so 100% in place never reads as done (Andres, 2026-09-26).
  const partlyPts = total ? (partly / total) * 100 : 0;
  return (
    <section className="card" aria-label={title}>
      <figure className="measure kpi">
        <Ring
          pct={p}
          partly={partlyPts}
          sub={p !== null ? fullyText(p, partlyPts) : undefined}
          median={median}
          label={`${title}: ${pct(p)}, ${inPlace} of ${total} in place, fully or partly (${partly} partly)${median !== null ? `, network median ${pct(median)}` : ''}. ${inPlaceSummary(p, partlyPts)}`}
        />
        <figcaption>
          <span className="kpi-name">{title}</span>
          <span className="kpi-count in-place-count">
            In place: {inPlace} of {total} ({pct(p)})
          </span>
          <span className="fully-line small">
            Fully in place: {fully} of {total} ({pct(total ? (fully / total) * 100 : null)})
          </span>
          <span className="partly-note small">{partlyText(partly)}</span>
          {median !== null && (
            <span className="legend small">
              <span className="key-median" aria-hidden="true" /> Network median {pct(median)}
            </span>
          )}
        </figcaption>
      </figure>
      {/* Full card width, directly under the headline, so the partly share reads at a glance. */}
      <InPlaceSplit fully={fully} partly={partly} notYet={notYet} notRated={Math.max(0, score.size - total)} label={title} />
      <p className="muted small kpi-note">{note}</p>
    </section>
  );
}

/**
 * A baseline as of March 2025, for a firm with no current results: the share of the surveyed controls the firm
 * reported achieved. Hatched and labelled as the firm's own answers; never set against the network median.
 */
function SurveyKpiCard({ score, measure }: { score: BaselineScore; measure: string }) {
  const surveyed = score.surveyed ?? score.assessed;
  const notCovered = score.notCovered ?? 0;
  return (
    <section className="card" aria-label={`${score.name}, as of March 2025`}>
      <figure className="measure kpi">
        <Ring
          hatched
          pct={score.pct}
          median={null}
          label={`${score.name}, ${measure}: ${pct(score.pct)}, ${score.inPlace} of ${surveyed} surveyed controls reported in place${notCovered ? `; ${notCovered} of the ${score.size} not covered by the survey` : ''}`}
        />
        <figcaption>
          <span className="kpi-name">{score.name}</span>
          <span className="asof-chip">As of March 2025</span>
          <span className="kpi-count">
            {score.inPlace} of {surveyed}
          </span>
          <span className="muted small">surveyed controls reported in place</span>
          {notCovered > 0 && (
            <span className="muted small">
              {notCovered} of the {score.size} not covered by the survey
            </span>
          )}
          <span className="legend small" title={measure}>
            <span className="key-hatch survey" aria-hidden="true" /> The firm's own answers, not an independent result
          </span>
        </figcaption>
      </figure>
    </section>
  );
}

/**
 * The areas table (DEC-49): Area, Controls reported in March 2025, Controls in place now and Share in place now.
 * Priority sits under the area name, so the rows still read highest priority first without a column of its own.
 * The share keeps fully and partly apart, as every other in-place chart does. Four columns fit 375 px.
 */
function AreasTable({ rows, firmCode }: { rows: AreaRow[]; firmCode: string }) {
  return (
    <>
      <div className="table-wrap areas-wrap" tabIndex={0} role="region" aria-label="Areas, highest priority first">
        <table className="areas-table">
          <caption className="sr-only">Controls per area, highest priority first: reported in March 2025, in place now, and the share in place now against the network median</caption>
          <thead>
            <tr>
              <th scope="col">Area</th>
              <th scope="col">Controls reported in March 2025</th>
              <th scope="col">Controls in place now</th>
              <th scope="col">Share in place now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const partlyPts = a.rated2026 ? (a.partly2026 / a.rated2026) * 100 : 0;
              return (
                <tr key={a.domainNumber}>
                  <th scope="row">
                    <Link to={`/firm/${firmCode}/controls?area=${a.domainNumber}`}>{a.area}</Link>
                    <span className="area-priority">
                      <span className={`risk risk-${a.risk}`}>{RISK_LABEL[a.risk]}</span>
                    </span>
                  </th>
                  {/* UX-14: the March 2025 share beside its count. */}
                  <td>
                    {a.surveyed2025 ? (
                      <>
                        {a.met2025} of {a.surveyed2025} met <span className="muted small">({pct((a.met2025 / a.surveyed2025) * 100)})</span>
                      </>
                    ) : (
                      'Not surveyed'
                    )}
                  </td>
                  <td>
                    {a.rated2026 ? (
                      <>
                        {a.met2026} of {a.rated2026} in place
                        {a.partly2026 > 0 && <span className="muted small area-partly"> ({a.partly2026} partly)</span>}
                      </>
                    ) : (
                      'Not rated'
                    )}
                  </td>
                  <td>
                    <div className="cell-bar">
                      <Bullet
                        pct={a.pct2026}
                        partly={partlyPts}
                        median={a.networkMedian}
                        label={`${a.area}: ${a.pct2026 === null ? 'n/a' : inPlaceSummary(a.pct2026, partlyPts)}${a.networkMedian !== null ? `, network median ${pct(a.networkMedian)}` : ''}`}
                      />
                      <span className="small">
                        {pct(a.pct2026)}
                        {a.pct2026 !== null && a.partly2026 > 0 && <span className="bar-fully"> ({fullyText(a.pct2026, partlyPts)})</span>}
                        {/* UX-14: the network median's value, which the tick on the bar marks. */}
                        {a.networkMedian !== null && <span className="muted">; network median {pct(a.networkMedian)}</span>}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <InPlaceKey />
      <p className="legend small">
        <span className="key-median" aria-hidden="true" /> Network median. <strong>Controls reported in March 2025:</strong> how many of the area's surveyed controls the firm
        reported as met in the March 2025 survey, its own answers. <strong>Controls in place now:</strong> in place now, fully or partly (National IT review, the CDW assessment, or
        the firm's current rating).
      </p>
      <ChartTable
        caption="Areas as a plain table"
        head={['Area', 'Priority', 'Controls reported in March 2025', 'Controls in place now, fully or partly', 'Of those, partly', 'Share in place now', 'Network median']}
        // CP-11: the same words as the visual table for what was not surveyed or rated, never "0 of 0".
        rows={rows.map((a) => [a.area, RISK_LABEL[a.risk], a.surveyed2025 ? `${a.met2025} of ${a.surveyed2025}` : 'Not surveyed', a.rated2026 ? `${a.met2026} of ${a.rated2026}` : 'Not rated', String(a.partly2026), pct(a.pct2026), pct(a.networkMedian)])}
      />
    </>
  );
}

